import { supabase } from './supabase';

// -------------------------------------------------------------------
// Property Management — CRUD operations for villa_properties
// -------------------------------------------------------------------

export interface RenderSettings {
    exposure: number;
    toneMapping: string;
    ambientIntensity: number;
    ambientColor: string;
    directIntensity: number;
    directColor: string;
    envIntensity: number;
    bloomIntensity: number;
    bloomLuminanceThreshold: number;
    vignetteOffset: number;
    vignetteDarkness: number;
    ssaoIntensity: number;
    ssaoRadius: number;
    fogDensity: number;
    modelScale: number;
    hideHdrBackground: boolean;
    showSunSlider: boolean;
}

// Custom button config — hide/show meshes on click
export interface CustomButton {
    label: string;
    icon: string;           // hugeicons class e.g. "hgi-roof"
    meshPattern: string;    // comma-separated mesh names or regex pattern
    action: 'toggle' | 'hide' | 'show';
}

export interface Property {
    id: string;
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    glb_model_url: string;
    thumbnail_url: string;
    form_email: string;
    form_subject_prefix: string;
    button_title: string;
    default_hdr: string;
    active_hdrs: string[];
    render_settings: RenderSettings;
    custom_buttons: CustomButton[];
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export type PropertyInput = Omit<Property, 'id' | 'created_at' | 'updated_at'>;

export const DEFAULT_RENDER_SETTINGS: RenderSettings = {
    exposure: -8.0,
    toneMapping: 'ACES Filmic',
    ambientIntensity: 0.5,
    ambientColor: '#ffffff',
    directIntensity: 0.8,
    directColor: '#ffffff',
    envIntensity: 1.0,
    bloomIntensity: 0.3,
    bloomLuminanceThreshold: 0.85,
    vignetteOffset: 0.35,
    vignetteDarkness: 0.4,
    ssaoIntensity: 2.0,
    ssaoRadius: 0.5,
    fogDensity: 0.0015,
    modelScale: 5,
    hideHdrBackground: false,
    showSunSlider: true,
};

// -------------------------------------------------------------------
// Fetch all properties
// -------------------------------------------------------------------

export async function fetchAllProperties(): Promise<Property[]> {
    const { data, error } = await supabase
        .from('villa_properties')
        .select('*')
        .order('created_at', { ascending: true });

    if (error) {
        console.error('[Properties] Failed to fetch:', error);
        return [];
    }

    return data || [];
}

// -------------------------------------------------------------------
// Fetch single property by slug
// -------------------------------------------------------------------

export async function fetchPropertyBySlug(slug: string): Promise<Property | null> {
    const { data, error } = await supabase
        .from('villa_properties')
        .select('*')
        .eq('slug', slug)
        .single();

    if (error) {
        console.error('[Properties] Failed to fetch by slug:', error);
        return null;
    }

    return data;
}

// -------------------------------------------------------------------
// Fetch single property by ID
// -------------------------------------------------------------------

export async function fetchPropertyById(id: string): Promise<Property | null> {
    const { data, error } = await supabase
        .from('villa_properties')
        .select('*')
        .eq('id', id)
        .single();

    if (error) {
        console.error('[Properties] Failed to fetch by id:', error);
        return null;
    }

    return data;
}

// -------------------------------------------------------------------
// Resolve property from URL path or ?p= param
// URL structure: 3d.augmento.com/{slug}
// -------------------------------------------------------------------

export async function resolveProperty(): Promise<Property | null> {
    // 1. Check URL pathname: /can-canto → slug "can-canto"
    const pathname = window.location.pathname;
    const pathSlug = pathname.replace(/^\/+|\/+$/g, ''); // strip leading/trailing slashes
    if (pathSlug && pathSlug !== 'viewer.html' && pathSlug !== 'index.html' && pathSlug !== 'dashboard.html' && !pathSlug.includes('.')) {
        const prop = await fetchPropertyBySlug(pathSlug);
        if (prop) return prop;
    }

    // 2. Fallback: check ?p=slug query param (backward compat / dev convenience)
    const urlParams = new URLSearchParams(window.location.search);
    const slugParam = urlParams.get('p');
    if (slugParam) {
        return fetchPropertyBySlug(slugParam);
    }

    // 3. No slug found — root URL now shows landing page
    return null;
}

// -------------------------------------------------------------------
// Create property
// -------------------------------------------------------------------

export async function createProperty(input: Partial<PropertyInput>): Promise<Property | null> {
    const { data, error } = await supabase
        .from('villa_properties')
        .insert({
            slug: input.slug || '',
            title: input.title || '',
            subtitle: input.subtitle || '',
            description: input.description || '',
            glb_model_url: input.glb_model_url || '',
            thumbnail_url: input.thumbnail_url || '',
            form_email: input.form_email || '',
            form_subject_prefix: input.form_subject_prefix || '',
            button_title: input.button_title || 'INTERESTED?',
            default_hdr: input.default_hdr || 'Golden Gate Hills',
            render_settings: input.render_settings || DEFAULT_RENDER_SETTINGS,
            is_active: input.is_active ?? true,
        })
        .select()
        .single();

    if (error) {
        console.error('[Properties] Failed to create:', error);
        throw error;
    }

    return data;
}

// -------------------------------------------------------------------
// Update property
// -------------------------------------------------------------------

export async function updateProperty(id: string, updates: Partial<PropertyInput>): Promise<void> {
    const { error } = await supabase
        .from('villa_properties')
        .update({
            ...updates,
            updated_at: new Date().toISOString(),
        })
        .eq('id', id);

    if (error) {
        console.error('[Properties] Failed to update:', error);
        throw error;
    }
}

// -------------------------------------------------------------------
// Delete property
// -------------------------------------------------------------------

export async function deleteProperty(id: string): Promise<void> {
    const { error } = await supabase
        .from('villa_properties')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('[Properties] Failed to delete:', error);
        throw error;
    }
}

// -------------------------------------------------------------------
// Slug generator
// -------------------------------------------------------------------

export function generateSlug(title: string): string {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 60);
}
